/**
 * Audit Log — V1 business-section semantics (parity port).
 *
 * V1 (`tracxtms` Laravel monolith) gave every audit row two things V2's
 * raw field-diff lacked:
 *   1. A curated **category** label per module (`audit_log2.audit_for`,
 *      built in `AuditLog2::auditForLabels()`) — e.g. the `drivers` module
 *      reads "Drivers and Carriers", not the mechanical "Driver".
 *   2. A **business section** grouping within a record (V1's per-module
 *      detail renderers + `getCustomData()` load sections — Billing,
 *      Drivers, Routing, Map, Other Charges, Margin, Notes) so the log
 *      could say "the Billing section changed", not just "3 fields changed".
 *
 * V2 stores a generic `{ field: { before, after } }` diff on every
 * `AuditLog.changes`. Rather than a schema migration + backfill, we derive
 * category + section **at read time** from this single source of truth,
 * shared by the server (list / CSV / SSRM decoration) and the client
 * (drawer grouping, grid column, module dropdown labels). Works on every
 * existing audit row with zero migration.
 */
import { RRF_FEATURE_KEY_BY_MODULE } from './routing-row-fields';
/** Section label used when no rule matches a changed field. */
export const AUDIT_SECTION_GENERAL = 'General';
/**
 * Routing Row Fields (WAYP-820) are stored one-per-module in a dedicated
 * `OrganizationFeature.configJson` (the `*_cfrw` feature keys). They're all
 * audited under the shared `OrganizationFeature` entityType, which would
 * otherwise bucket every module's edit under the generic "Enterprise Features"
 * category. Map each module's feature key → a module-scoped "Routing Row Fields"
 * category so a Dispatch-tab edit reads as Dispatch in the audit log (and the
 * Category filter can target one module). Keyed by the audit `entityId`, which
 * for OrganizationFeature rows IS the feature key.
 */
const RRF_MODULE_CATEGORY_LABEL = {
    dispatch: 'Dispatch',
    usps: 'USPS Carrier',
    intermodal: 'Intermodal',
    ftlbrokerage: 'Full Truckload Brokerage',
    livestock: 'Livestock',
};
export const AUDIT_ORG_FEATURE_CATEGORY_BY_KEY = Object.fromEntries(Object.entries(RRF_FEATURE_KEY_BY_MODULE).map(([module, featureKey]) => [
    featureKey,
    `${RRF_MODULE_CATEGORY_LABEL[module]} — Routing Row Fields`,
]));
/** Reverse lookup: an RRF category label → its OrganizationFeature key(s). */
const ORG_FEATURE_KEYS_BY_CATEGORY = (() => {
    const map = {};
    for (const [key, label] of Object.entries(AUDIT_ORG_FEATURE_CATEGORY_BY_KEY)) {
        (map[label] ?? (map[label] = [])).push(key);
    }
    return map;
})();
/**
 * OrganizationFeature keys (entityIds) belonging to a module-scoped Routing Row
 * Fields category. Returns `[]` for any non-RRF category so the caller falls
 * back to the normal entityType-based filter.
 */
export function getOrgFeatureKeysForCategory(category) {
    return ORG_FEATURE_KEYS_BY_CATEGORY[category] ?? [];
}
/**
 * entityType → curated business-category label (V1 `category_name`).
 *
 * Keys are the `entityType` strings the V2 `audit()` middleware emits
 * (see `ENTITY_TYPE_TO_MODEL` in audit.ts) — both the V1-parity module
 * names (`'Dispatch Loads'`, `'USPS Trips'`) and the legacy PascalCase
 * model names, so historical rows resolve too. Anything not listed falls
 * back to the camelCase split in `getAuditCategoryLabel`.
 */
export const AUDIT_CATEGORY_LABELS = {
    // Dispatch / freight
    Load: 'Dispatch',
    'Dispatch Loads': 'Dispatch',
    LoadStatus: 'Load Status',
    LoadNote: 'Dispatch',
    // Per-user Load Form layout changes (view mode, split, section order, and the
    // Routing table's show/hide + column order). Emitted from the table-state
    // controller via a direct auditService.log call (see user.controller.ts).
    'Dispatch Load Form': 'Dispatch',
    Quote: 'Quote',
    Quotes: 'Quote',
    // Carriers / drivers
    Driver: 'Drivers and Carriers',
    Drivers: 'Drivers and Carriers',
    Carrier: 'Drivers and Carriers',
    Carriers: 'Drivers and Carriers',
    CarrierRequirement: 'Carrier Requirement Types',
    Supplier: 'Supplier',
    // Customers / locations
    Customer: 'Customers',
    Location: 'Locations',
    Locations: 'Locations',
    // Equipment
    Equipment: 'Equipment',
    EquipmentTypeConfig: 'Equipment Types',
    ExpenseTypeConfig: 'Expense Types',
    // Rating
    BillingRate: 'Rating Billing Rates',
    'Billing Rates': 'Rating Billing Rates',
    FscRate: 'Rating FSC Rates',
    'FSC Rates': 'Rating FSC Rates',
    AccessorialRate: 'Rating Accessorial Rates',
    'Accessorial Rates': 'Rating Accessorial Rates',
    // Lane City + Lane Location rates share the `LaneRate` entityType (the mode is
    // an org setting, not a row distinction the audit filter can target in SQL),
    // so they collapse into one V1-parity category. The Lane City vs Lane Location
    // context is carried by the entity name ("Chicago, IL → New York, NY" vs a
    // resolved location pair) and the Pick/Drop fields.
    LaneRate: 'Rating Lane Rates',
    // Charges / deductions
    AccessorialCharge: 'Other Charges',
    AccessorialType: 'Other Charges or Reimbursement Type',
    Deduction: 'Deductions and Reimbursements',
    // The Deductions / Reimbursements page audits under `DriverDeduction`
    // (deduction.routes.ts). V1's audit-log category for this module is
    // "Deductions and Reimbursements" (AuditLog2::auditForLabels →
    // `audit_deductions.category_name`), NOT the mechanical "Driver Deduction"
    // the camelCase fallback produced. Aliased alongside the legacy `Deduction`
    // key so both entityTypes collapse into the one V1 category.
    DriverDeduction: 'Deductions and Reimbursements',
    DeductionReimbursementType: 'Deduction or Driver Reimbursement Type',
    CustomCharge: 'Custom Charges',
    'Other Charge / Reimbursement Types': 'Other Charges or Reimbursement Type',
    // Finance
    Invoice: 'Invoices',
    Settlement: 'Settlements',
    Payment: 'Payments',
    ReceivedPayment: 'Received Payments',
    DriverLoan: 'Loans',
    Loans: 'Loans',
    Expenses: 'Expenses',
    ManualFuel: 'Manual Fuel',
    'Manual Fuel': 'Manual Fuel',
    FuelCard: 'Fuel Cards',
    EscrowAccount: 'Escrow Accounts',
    'Escrow Accounts': 'Escrow Accounts',
    CustomerBillingContract: 'Customer Billing Contracts',
    // Work / purchase orders
    WorkOrder: 'Work Order',
    'Work Orders': 'Work Order',
    PurchaseOrder: 'Purchase Order',
    // USPS
    'USPS Load': 'USPS Carrier',
    'USPS Trips': 'USPS Trips',
    'USPS Contract': 'USPS Contracts',
    'USPS Recurring Loads': 'USPS Recurring Loads',
    'USPS 5500': 'USPS 5500',
    'Reconcile Slips': 'Reconcile Slips',
    Slips: 'USPS Slips',
    'Driver Route': 'Driver Routes',
    UspsMailClass: 'USPS Mail Class Types',
    UspsMailPurpose: 'USPS Mail Purpose Types',
    // Config / admin
    CustomDispatchTab: 'Custom Dispatch Tab',
    CustomTemplate: 'Custom Templates',
    CustomsBroker: 'Customs Broker',
    'Customs Broker': 'Customs Broker',
    'Customs Brokers': 'Customs Broker',
    DepartmentType: 'Department Type',
    TimeOffType: 'Time Off Types',
    TradingPartner: 'Trading Partner',
    Holidays: 'Holidays',
    'File Checklist': 'File Checklist',
    OrganizationFeature: 'Enterprise Features',
    SupportTicket: 'Support Tickets',
    DockAppointment: 'Dock Appointments',
    DockDoor: 'Dock Doors',
    CrossDockFacility: 'Cross-Dock Facilities',
    StagingArea: 'Staging Areas',
    // feat/audit-log-full-coverage: full-application coverage. V1-parity
    // category labels for the newly-audited surfaces. (Modules not listed here
    // fall back to the title-cased entityType, which already reads sensibly —
    // e.g. "Module Settings", "Dashboard Permissions", "GPS Provider".)
    Authentication: 'Sign In / Sign Out', // V1 `audit_login`
    Users: 'User', // V1 `audit_users`
    'User Invitations': 'User',
};
/**
 * The full canonical set of business categories (V1 audit-log category
 * dropdown), distinct + alphabetically sorted. Drives the Category filter
 * so every V1 category is selectable regardless of whether the current
 * org has data for it yet (V1 showed the fixed list, not just
 * data-present modules).
 */
export const AUDIT_CATEGORY_OPTIONS = [
    ...new Set([
        ...Object.values(AUDIT_CATEGORY_LABELS),
        // Per-module Routing Row Fields categories are real, selectable categories
        // even though they share the OrganizationFeature entityType.
        ...Object.values(AUDIT_ORG_FEATURE_CATEGORY_BY_KEY),
    ]),
].sort((a, b) => a.localeCompare(b));
/**
 * Reverse lookup: a category label → every entityType that maps to it.
 * Multiple V2 entityTypes collapse into one V1 category (e.g.
 * `Driver` / `Drivers` / `Carrier` / `Carriers` → "Drivers and Carriers";
 * `Load` / `Dispatch Loads` / `USPS Load` → "Dispatch"). Filtering by a
 * category must therefore match `entityType IN (...)`, not a single value.
 */
const ENTITY_TYPES_BY_CATEGORY = (() => {
    const map = {};
    for (const [entityType, label] of Object.entries(AUDIT_CATEGORY_LABELS)) {
        (map[label] ?? (map[label] = [])).push(entityType);
    }
    return map;
})();
/**
 * Every entityType belonging to a category label. Returns `[]` for an
 * unknown label so callers can skip the filter rather than match nothing.
 */
export function getEntityTypesForCategory(category) {
    return ENTITY_TYPES_BY_CATEGORY[category] ?? [];
}
/**
 * Past-tense action verbs (V1 `event_type_name` semantics) used to build
 * the human-readable event description. `approve` is included for forward
 * compatibility — V1 emitted an "Approved" event type; V2 currently routes
 * approvals through `status_change`, but if an `approve` row ever lands it
 * renders correctly here.
 */
export const AUDIT_ACTION_PAST_TENSE = {
    create: 'Created',
    update: 'Modified',
    delete: 'Deleted',
    restore: 'Restored',
    status_change: 'Status Changed',
    approve: 'Approved',
    login: 'Login',
    export: 'Exported',
    import: 'Imported',
    system_event: 'System Event',
};
/**
 * Explicit per-entityType field → section maps. Only the modules whose
 * records have genuinely distinct business sections need an entry; every
 * other field falls through to the keyword heuristic below.
 *
 * The Load map mirrors V1's load tabs (Billing / Drivers & Equipment /
 * Routing / Other Charges / Margin / Notes), the same sections V1's
 * `getCustomData()` bucketed custom fields into.
 */
const SECTION_BILLING = 'Billing';
const SECTION_DRIVERS = 'Drivers & Equipment';
const SECTION_ROUTING = 'Routing';
const SECTION_OTHER_CHARGES = 'Other Charges';
const SECTION_MARGIN = 'Margin';
const SECTION_NOTES = 'Notes';
const SECTION_STATUS = 'Status';
const SECTION_DOCUMENTS = 'Documents';
const LOAD_FIELD_SECTIONS = {
    // Billing
    customerId: SECTION_BILLING,
    billToId: SECTION_BILLING,
    billingCurrency: SECTION_BILLING,
    customerReference: SECTION_BILLING,
    poNumber: SECTION_BILLING,
    invoiceId: SECTION_BILLING,
    trailerRentAmount: SECTION_BILLING,
    trailerRentTaxId: SECTION_BILLING,
    // Drivers & equipment
    driverId: SECTION_DRIVERS,
    carrierId: SECTION_DRIVERS,
    truckId: SECTION_DRIVERS,
    trailerId: SECTION_DRIVERS,
    equipment1Id: SECTION_DRIVERS,
    equipment2Id: SECTION_DRIVERS,
    // Routing
    stops: SECTION_ROUTING,
    routingRowLinkId: SECTION_ROUTING,
    miles: SECTION_ROUTING,
    distance: SECTION_ROUTING,
    // Status
    status: SECTION_STATUS,
    statusId: SECTION_STATUS,
};
const AUDIT_FIELD_SECTIONS = {
    Load: LOAD_FIELD_SECTIONS,
    'Dispatch Loads': LOAD_FIELD_SECTIONS,
    'USPS Load': LOAD_FIELD_SECTIONS,
};
/**
 * Keyword heuristic — classifies a field into a business section by name
 * when no explicit per-module rule applies. Generalises the V1 section
 * concept across every audited module without hand-mapping each one.
 * Ordered: first matching rule wins.
 */
const SECTION_KEYWORD_RULES = [
    { section: SECTION_NOTES, test: /note|comment|remark/i },
    { section: SECTION_DOCUMENTS, test: /document|attachment|file|upload/i },
    { section: SECTION_MARGIN, test: /margin|profit|cost(?!ume)/i },
    { section: SECTION_OTHER_CHARGES, test: /accessorial|othercharge|other_charge|surcharge|fee/i },
    { section: SECTION_BILLING, test: /bill|invoic|rate|currenc|tax|charge|price|amount|payment|fsc/i },
    { section: SECTION_DRIVERS, test: /driver|carrier|truck|trailer|equipment|tractor/i },
    { section: SECTION_ROUTING, test: /stop|pickup|delivery|origin|destination|rout|lane|mile|distance|appointment|address|geofence/i },
    { section: SECTION_STATUS, test: /status|state|approv|stage/i },
];
/**
 * Curated business-category label for an audit module. Falls back to a
 * camelCase / underscore split so unmapped entity types still read
 * sensibly (e.g. `RoutingRowType` → "Routing Row Type").
 */
export function getAuditCategoryLabel(entityType, entityId) {
    // OrganizationFeature rows overload one entityType across many features; the
    // Routing Row Fields (`*_cfrw`) ones get a module-scoped category keyed by the
    // feature key (entityId). Every other feature keeps the generic label below.
    if (entityType === 'OrganizationFeature' && entityId) {
        const rrf = AUDIT_ORG_FEATURE_CATEGORY_BY_KEY[entityId];
        if (rrf)
            return rrf;
    }
    const mapped = AUDIT_CATEGORY_LABELS[entityType];
    if (mapped)
        return mapped;
    return entityType
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
        .trim();
}
/** Business section for a single changed field on a given module. */
export function getAuditFieldSection(entityType, field) {
    const explicit = AUDIT_FIELD_SECTIONS[entityType]?.[field];
    if (explicit)
        return explicit;
    for (const rule of SECTION_KEYWORD_RULES) {
        if (rule.test.test(field))
            return rule.section;
    }
    return AUDIT_SECTION_GENERAL;
}
/**
 * Distinct business sections touched by a `changes` payload, in a stable
 * display order. This is the "which section changed" summary V1 surfaced
 * (`modifiedHeaders`). Internal discriminator keys (`_verb`, etc.) and
 * common system columns are ignored.
 */
const SECTION_ORDER = [
    SECTION_STATUS,
    SECTION_BILLING,
    SECTION_DRIVERS,
    SECTION_ROUTING,
    SECTION_OTHER_CHARGES,
    SECTION_MARGIN,
    SECTION_NOTES,
    SECTION_DOCUMENTS,
    AUDIT_SECTION_GENERAL,
];
const SECTION_IGNORED_FIELDS = new Set([
    'id', 'organizationId', 'createdAt', 'updatedAt', 'deletedAt',
]);
export function computeChangedSections(entityType, changes) {
    if (!changes)
        return [];
    const found = new Set();
    for (const field of Object.keys(changes)) {
        if (field.startsWith('_') || SECTION_IGNORED_FIELDS.has(field))
            continue;
        found.add(getAuditFieldSection(entityType, field));
    }
    return SECTION_ORDER.filter((s) => found.has(s));
}
/**
 * Human-readable event description (V1 `event_meta_description`):
 *   create → "New Dispatch Created (WP-12345)"
 *   other  → "Dispatch Modified (WP-12345)"
 */
export function buildAuditDescription(entityType, action, entityName, entityId) {
    const label = getAuditCategoryLabel(entityType, entityId);
    const verb = AUDIT_ACTION_PAST_TENSE[action] ?? action;
    const suffix = entityName ? ` (${entityName})` : '';
    if (action === 'create')
        return `New ${label} Created${suffix}`;
    return `${label} ${verb}${suffix}`;
}
//# sourceMappingURL=audit-sections.js.map